'use client';

import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { WithScrollToItemOnMountWrapper } from '@portals/hc-rich-editor';
import { useUserSawHalfArticleBlockAnalytic } from '@portals/help-center-core';
import { getArticleContentAnalyticHandler } from '@/_analytic/events/article-content-click-event';
import { getDocument, getWindow, getElementById } from '@/_utils';
import { useUserSawArticleEnd } from '@/_analytic/hooks/use-user-saw-article-end-analytic';
import { userSawArticleEndEvent } from '@/_analytic/events/user-saw-article-end-event';
import { BOTTOM_LINE_NAME } from '@/_analytic/hooks/use-user-saw-article-end-analytic/_constants';
import { IMaterial } from '@/_types';
import { TAnalyticEventParams } from '@/_analytic/_types';
import { useMaterialAnalyticData } from '@/_analytic/hooks/use-material-analytic-data';
import { useAnalytic } from './_hooks/use-analytic';
import styles from './index.module.scss';

const BLOCK_NAME = 'Client-Article';
const cn = classnames.bind(styles);

type TClientArticleProps = PropsWithChildren<{
  material: IMaterial;
  idsRecommendBlock: TAnalyticEventParams['idsRecommendBlock'];
  scrollContainerId?: string;
  byAnchorLink?: TAnalyticEventParams['byAnchorLink'];
}>;

export const ClientArticle = ({
  children,
  material,
  idsRecommendBlock,
  scrollContainerId,
  byAnchorLink: propsByAnchorLink,
}: TClientArticleProps) => {
  const byAnchorLink = useMemo(
    () => propsByAnchorLink ?? Boolean(getWindow()?.location.hash),
    [propsByAnchorLink],
  );

  useAnalytic({
    material,
    idsRecommendBlock,
    byAnchorLink,
  });

  const commonAnalyticParams = useMaterialAnalyticData({
    materialId: material.id,
    materialTitle: material.title,
    materialCategoryId: material.categoryIds?.[0] ?? '',
  });
  const commonAnalyticParamsWithAnchor = useMemo(
    () => ({
      ...commonAnalyticParams,
      byAnchorLink,
    }),
    [byAnchorLink, commonAnalyticParams],
  );

  const handleAnalytic = getArticleContentAnalyticHandler(commonAnalyticParams);

  const [articleRef, setArticleRef] = useState<HTMLDivElement | null>(null);

  const scrollContainer =
    getElementById(scrollContainerId) || getDocument()?.body || null;

  useUserSawHalfArticleBlockAnalytic({
    content: articleRef,
    scrollElement: scrollContainer,
    params: commonAnalyticParamsWithAnchor,
  });

  const handleUserSawArticleEnd = () => {
    userSawArticleEndEvent(commonAnalyticParams);
  };

  useUserSawArticleEnd({
    content: articleRef,
    scrollElement: scrollContainer,
    onEvent: handleUserSawArticleEnd,
  });

  // грязный хак
  // scroll: false не работает как ожидается
  // https://github.com/vercel/next.js/discussions/64435
  const canScrollToTop = !getWindow()?.location.hash;
  useEffect(() => {
    if (canScrollToTop) {
      window?.scroll({
        top: 0,
        left: 0,
      });
    }
  }, [canScrollToTop]);

  return (
    <div
      ref={setArticleRef}
      className={cn(BLOCK_NAME)}
      onClick={handleAnalytic}
      role="button"
      tabIndex={0}
    >
      <WithScrollToItemOnMountWrapper>
        <>
          {children}
          <div
            className={cn(`${BLOCK_NAME}__analytic-scroll-end`)}
            data-name={BOTTOM_LINE_NAME}
          />
        </>
      </WithScrollToItemOnMountWrapper>
    </div>
  );
};
